import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { AlertTriangle, Check, Copy, ExternalLink, File, ShoppingBag, User, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../../Shared/api';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Campaign, CampaignType, DonationFields, Payment, PaymentMethodType, Project } from '../../../Shared/types';
import { AButton, Button } from '../../../Shared/UI/Button';
import { ColorBadge } from '../../../Shared/UI/ColorBadge';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import { PickerItem } from '../../../Shared/UI/Picker';
import { useCopy } from '../../../Shared/useCopy';
import { useRouter } from '../../router';
import ProjectIcon from '../../Settings/Projects/ProjectIcon';
import { Donation, DonationForm, Product, Purchase, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { DonationsLayout } from '../DonationsLayout';
import PaymentStatus from '../PaymentStatusLabel';
import EditDonationForm from './EditDonationForm';

export interface DonationPageProps extends SharedProps {
  donation: Donation & DonationFields & {
    campaign: (Campaign & {
      project: Project;
    }) | null;
    donation_form: DonationForm | null;
    payment: Payment;
    billing_url: string;
    payment_method: PaymentMethodType;
    purchases: (Purchase & {
      product: Product;
    })[];
    deletable: boolean;
  };
  projects: PickerItem[];
  donationForms: PickerItem[];
}

export default function DonationPage({
  donation,
  projects,
  donationForms,
  charity,
}: DonationPageProps) {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, formatNumber } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const totalAmount = donation.amount + donation.purchases.reduce((sum, purchase) => sum + purchase.amount, 0);

  const [copyBillingUrl, copiedBillingUrl] = useCopy(donation.billing_url);

  const [deleteDonation, { loading: deleting }] = useMutation(routes.delete_donation(donation.id), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <div>
      <Header>
        <Container>
          <div className="flex items-center justify-between w-full">
            <HeaderTitle>
              <ColorBadge
                color={charity.brand_color}
                name={donation.name || donation.email || t('backend:anonymous')}
                className="mr-3"
              />
              {donation.name || donation.email || t('backend:anonymous')}
            </HeaderTitle>
          </div>
        </Container>
      </Header>

      <Container>
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-3 space-y-4 text-slate-600">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:donation_amount')}
              </h4>
              {formatCurrency(donation.amount)}
            </div>

            {donation.purchases.length > 0 && (
              <>
                <div>
                  <h4 className="font-medium text-slate-500">
                    {t('backend:purchases')}
                  </h4>
                  {donation.purchases.map((purchase) => (
                    <div key={purchase.id}>
                      {formatNumber(purchase.quantity)}
                      {'× '}
                      <Link href={routes.product_page(purchase.product.id)}>
                        <Icon className="mr-1">
                          <ShoppingBag />
                        </Icon>
                        {purchase.product.title}
                      </Link>
                      {' ('}
                      {formatCurrency(purchase.amount)}
                      )
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-medium text-slate-500">
                    {t('backend:total_amount')}
                  </h4>
                  {formatCurrency(totalAmount)}
                </div>
              </>
            )}

            {donation.campaign && (
              <>
                <div>
                  <h4 className="font-medium text-slate-500">
                    {t('backend:campaign')}
                  </h4>
                  <Link href={routes.campaign_page(donation.campaign.id)}>
                    <Icon className="mr-1">
                      {donation.campaign.type === CampaignType.Individual && <User />}
                      {donation.campaign.type === CampaignType.Team && <Users />}
                    </Icon>
                    {donation.campaign.title}
                  </Link>
                </div>

                <div>
                  <h4 className="font-medium text-slate-500">
                    {t('backend:project')}
                  </h4>
                  <Link href={routes.project_page(donation.campaign.project.id)}>
                    <ProjectIcon project={donation.campaign.project} className="mr-1" />
                    {donation.campaign.project.title}
                  </Link>
                </div>
              </>
            )}

            {donation.donation_form && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:donation_form')}
                </h4>
                <Link href={routes.donation_form_page(donation.donation_form.id)}>
                  <Icon className="mr-1">
                    <File />
                  </Icon>
                  {donation.donation_form.internal_title}
                </Link>
              </div>
            )}

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:status')}
              </h4>
              <PaymentStatus payment={donation.payment} />
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:payment_method')}
              </h4>
              {t(`shared:payment_method.${donation.payment_method}`)}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:date')}
              </h4>
              {formatDate(donation.created_at, 'display_date_time')}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:payment_page')}
              </h4>
              <div className="flex space-x-2">
                <Input
                  type="text"
                  value={donation.billing_url}
                  readOnly
                  onFocus={(event) => event.target.select()}
                  inputSize="sm"
                />
                <Button
                  onClick={() => copyBillingUrl()}
                  variant="secondary"
                  className={classNames(copiedBillingUrl && '!text-slate-400')}
                  title={t('backend:copy')}
                  size="sm"
                >
                  <Icon>
                    {copiedBillingUrl ? <Check /> : <Copy />}
                  </Icon>
                </Button>
                <AButton
                  href={donation.billing_url}
                  target="_blank" rel="noopener noreferrer"
                  variant="secondary"
                  data-supporta-embed="false"
                  title={t('backend:open')}
                  size="sm"
                >
                  <Icon>
                    <ExternalLink />
                  </Icon>
                </AButton>
              </div>
            </div>

            {donation.deletable && (
              <div>
                <ConfirmButton
                  onClick={() => deleteDonation()}
                  loading={deleting}
                  variant="danger"
                  size="sm"
                >
                  {t('backend:delete_donation')}
                </ConfirmButton>
              </div>
            )}
          </div>

          <div className="col-span-9">
            <EditDonationForm
              donation={donation}
              projects={projects}
              donationForms={donationForms}
              charity={charity}
            />
          </div>
        </div>
      </Container>
    </div>
  );
}

DonationPage.layout = (page: ReactNode) => <Layout><DonationsLayout>{page}</DonationsLayout></Layout>;
